import { TrackManagerApplication } from "@/types/shared";
import {
  Badge,
  Flex,
  Text,
} from "@radix-ui/themes";
import {
  AccessorFnColumnDef,
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";
import { dateRangeFilter } from "@/shared/tables/utility";
import { badgeColors } from "@/shared/constants/constants";
import BasicTable from "../BasicTable";

interface Props {
  applications: TrackManagerApplication[];
  setSelectedRowIndex: React.Dispatch<React.SetStateAction<number | null>>;
  setFilteredRows: React.Dispatch<
    React.SetStateAction<Row<TrackManagerApplication>[] | null | undefined>
  >;
  /** `"event"` drops the Event and Location columns, which are the same value
   *  in every row once the table is showing one event's applications. */
  scope?: "track" | "event";
}

export default function ApplicationsTable({
  applications,
  setSelectedRowIndex,
  setFilteredRows,
  scope = "track",
}: Props) {
  const [data, setData] = useState(() => [...applications]);

  useEffect(() => {
    setData([...applications]);
  }, [applications]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );

  const columnHelper = createColumnHelper<TrackManagerApplication>();

  const columns = useMemo<ColumnDef<TrackManagerApplication, any>[]>(
    () => [
      columnHelper.accessor("participant.name", {
        header: "Applicant",
        accessorFn: (row) => `${row.participant.name} ${row.participant.email}`,
        cell: (props) => (
          <Flex direction="column">
            <Text>{props.row.original.participant.name}</Text>
            <Text color="gray" size="1">
              {props.row.original.participant.email}
            </Text>
          </Flex>
        ),
        meta: { filterVariant: "text" },
      } as AccessorFnColumnDef<TrackManagerApplication, any>),
      columnHelper.accessor("participant.participantable_type", {
        header: "Type",
        accessorFn: (row) => row.participant.participantable_type,
        meta: {
          filterVariant: "select",
          selectOptions: [
            { value: "all", title: "All" },
            { value: "MediaGroup", title: "Media" },
            { value: "DriverCar", title: "Driver" },
            { value: "Staff", title: "Staff" },
            { value: "Team", title: "Team" },
          ],
        },
      } as AccessorFnColumnDef<TrackManagerApplication, any>),
      ...(scope === "event"
        ? []
        : [
            columnHelper.accessor("event_name", {
              header: "Event",
              meta: { filterVariant: "text" },
            }),
            columnHelper.accessor("event_location", {
              header: "Location",
              meta: { filterVariant: "text" },
            }),
            columnHelper.accessor("event_date", {
              header: "Date",
              cell: (props) => props.row.original.event_date,
              filterFn: dateRangeFilter,
              meta: { filterVariant: "dateRange" },
            }),
          ]),
      columnHelper.accessor("created_at_raw", {
        header: "Applied On",
        cell: (props) => props.row.original.created_at,
        filterFn: dateRangeFilter,
        meta: { filterVariant: "dateRange" },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (props) => (
          <Badge variant="soft" color={badgeColors[props.getValue()]}>
            <Text as="p" style={{ textTransform: "capitalize" }}>
              {props.getValue()}
            </Text>
          </Badge>
        ),
        meta: {
          filterVariant: "select",
          selectOptions: [
            { value: "all", title: "All" },
            { value: "pending", title: "Pending" },
            { value: "approved", title: "Approved" },
            { value: "waitlisted", title: "Waitlisted" },
            { value: "rejected", title: "Rejected" },
            { value: "withdrawn", title: "Withdrawn" },
          ],
        },
      }),
    ],
    [scope],
  );
  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const filteredRows = table.getRowModel().rows;

  useEffect(() => {
    setFilteredRows(filteredRows);
  }, [filteredRows, columnFilters]);
  return (
        <BasicTable
          table={table}
          handleRowOnClick={(_: Row<TrackManagerApplication>, idx?: number) => idx !== undefined && setSelectedRowIndex(idx)}
        /> 
  );
}
